import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useMemo } from "react";
import CopyIdButton from "../components/CopyIdButton";
import { useTableColumnFilters, FilterTh } from "../components/tableFilters";
import { Order, OrderStatus, OrderUnit, ModelCost } from "../types";
import {
  orderService,
  costService,
} from "../services/dataService";
import { turkishIncludes, formatCurrency } from "../utils/formatters";
import PageLoader from "../components/PageLoader";
import ImageManager from "../components/ImageManager";
import AlertModal from "../components/AlertModal";
import OrderDetailModal from "../components/OrderDetailModal";
import SendDocumentModal from "../components/SendDocumentModal";
import InvoiceNumberModal from "../components/InvoiceNumberModal";
import { FirmMultiPickerField } from "../components/FirmPicker";
import { usePermissions } from "../context/PermissionContext";
import "./PurchaseOrders.css";
import "./AccountingOrders.css";

const STORAGE_KEY = "accounting-orders-filters";

type InvoiceFilter = "all" | "filled" | "empty";
const INVOICE_CHIPS: [InvoiceFilter, string][] = [["all", "Tümü"], ["empty", "Faturalanacak"], ["filled", "Faturalandı"]];

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20 4v5h-5" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  filterOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18l-7 8v6l-4-2v-4z" /><path d="M17 17l4 4M21 17l-4 4" /></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
  more: <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>,
  doc: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M16 13H8M16 17H8" /></svg>,
  money: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1v22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
  pen: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>,
  truck: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" /><circle cx="7" cy="18" r="1.6" /><circle cx="17" cy="18" r="1.6" /></svg>,
  image: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>,
  info: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>,
};

const fmt2 = (n: number) => n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const orderCurrency = (o: Order) => o.priceCurrency || o.currency || "TRY";
const hasInvoiceNo = (o: Order) => !!o.invoiceNumber && o.invoiceNumber.trim() !== "";
/** Siparişlerin tutarını döviz bazında toplar: "1.250,00 USD · 1.000,00 TRY" (en büyük iki döviz). */
const sumByCurrency = (list: Order[]) => {
  const m: Record<string, number> = {};
  list.forEach((o) => {
    if (o.price && o.quantity) m[orderCurrency(o)] = (m[orderCurrency(o)] || 0) + o.price * o.quantity;
  });
  const parts = Object.entries(m).sort((a, b) => b[1] - a[1]);
  if (parts.length === 0) return "";
  return parts.slice(0, 2).map(([c, v]) => `${fmt2(v)} ${c}`).join(" · ") + (parts.length > 2 ? ` +${parts.length - 2}` : "");
};

interface CostDetailModal {
  isOpen: boolean;
  orderId: string;
  modelId: string;
  modelCode: string;
  firmCode: string;
  firmName: string;
  quantity: number;
  costs: ModelCost[];
  loading: boolean;
  totalCost: number;
  costWithOverhead: number;
  finalCostWithProfit: number;
}

// localStorage'dan filtreleri yükle
const loadFiltersFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        firmFilter: parsed.firmFilter || [],
        dateFilter: parsed.dateFilter || { startDate: "", endDate: "" },
        searchTerm: parsed.searchTerm || "",
        invoiceFilter: parsed.invoiceFilter || "all",
      };
    }
  } catch (error) {
    console.error("Failed to load filters from storage:", error);
  }
  return {
    firmFilter: [],
    dateFilter: { startDate: "", endDate: "" },
    searchTerm: "",
    invoiceFilter: "all" as InvoiceFilter,
  };
};

const AccountingOrders: React.FC = () => {
  const { has, hasModule } = usePermissions(); // yetki matrisi
  const navigate = useNavigate();
  const savedFilters = loadFiltersFromStorage();

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  // İlk yüklemede tam sayfa yükleyici; sonraki yüklemelerde (süzgeç/yenile) kart soluklaşır, araç çubuğu yerinde kalır.
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>(savedFilters.searchTerm);
  const [invoiceFilter, setInvoiceFilter] = useState<InvoiceFilter>(savedFilters.invoiceFilter);
  const [firmFilter, setFirmFilter] = useState<string[]>(
    savedFilters.firmFilter,
  );
  const [dateFilter, setDateFilter] = useState({
    startDate: savedFilters.dateFilter.startDate || "",
    endDate: savedFilters.dateFilter.endDate || "",
  });
  const [showImageManager, setShowImageManager] = useState(false);
  const [selectedOrderForImages, setSelectedOrderForImages] = useState<
    string | null
  >(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Detail Modal state
  const [detailModal, setDetailModal] = useState<{
    isOpen: boolean;
    orderId: string;
  }>({ isOpen: false, orderId: "" });

  const [costDetailModal, setCostDetailModal] = useState<CostDetailModal>({
    isOpen: false,
    orderId: "",
    modelId: "",
    modelCode: "",
    firmCode: "",
    firmName: "",
    quantity: 0,
    costs: [],
    loading: false,
    totalCost: 0,
    costWithOverhead: 0,
    finalCostWithProfit: 0,
  });

  const openCostDetail = async (order: Order) => {
    setCostDetailModal({
      isOpen: true,
      orderId: order.orderId,
      modelId: order.modelId,
      modelCode: order.model?.modelCode || "",
      firmCode: order.firm?.firmCode || "",
      firmName: order.firm?.firmName || "",
      quantity: order.quantity || 0,
      costs: [],
      loading: true,
      totalCost: 0,
      costWithOverhead: 0,
      finalCostWithProfit: 0,
    });
    try {
      const costs = await costService.getModelCosts(order.orderId);
      const toTRY = (
        amount: number,
        currency: string,
        usdRate?: number,
        eurRate?: number,
        gbpRate?: number,
      ) => {
        if (currency === "TRY" || currency === "TL") return amount;
        if (currency === "USD" && usdRate) return amount * usdRate;
        if (currency === "EUR" && eurRate) return amount * eurRate;
        if (currency === "GBP" && gbpRate) return amount * gbpRate;
        return 0;
      };
      const totalCost = costs.reduce(
        (sum, c) =>
          sum +
          toTRY(
            c.totalCost || 0,
            c.currency || "TRY",
            c.usdRate,
            c.eurRate,
            c.gbpRate,
          ),
        0,
      );
      const costWithOverhead = costs.reduce(
        (sum, c) =>
          sum +
          toTRY(
            c.costWithOverhead || c.totalCost || 0,
            c.currency || "TRY",
            c.usdRate,
            c.eurRate,
            c.gbpRate,
          ),
        0,
      );
      const finalCostWithProfit = costs.reduce(
        (sum, c) =>
          sum +
          toTRY(
            c.finalCostWithProfit || c.totalCost || 0,
            c.currency || "TRY",
            c.usdRate,
            c.eurRate,
            c.gbpRate,
          ),
        0,
      );
      setCostDetailModal((prev) => ({
        ...prev,
        costs,
        loading: false,
        totalCost,
        costWithOverhead,
        finalCostWithProfit,
      }));
    } catch (error) {
      setCostDetailModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const closeCostDetail = () => {
    setCostDetailModal({
      isOpen: false,
      orderId: "",
      modelId: "",
      modelCode: "",
      firmCode: "",
      firmName: "",
      quantity: 0,
      costs: [],
      loading: false,
      totalCost: 0,
      costWithOverhead: 0,
      finalCostWithProfit: 0,
    });
  };

  const convertCostToTRY = (cost: ModelCost): number => {
    const amount = cost.totalCost || 0;
    const currency = cost.currency || "TRY";
    if (currency === "TRY" || currency === "TL") return amount;
    if (currency === "USD" && cost.usdRate) return amount * cost.usdRate;
    if (currency === "EUR" && cost.eurRate) return amount * cost.eurRate;
    if (currency === "GBP" && cost.gbpRate) return amount * cost.gbpRate;
    return 0;
  };

  // Fason flag state - her sipariş için maliyetlerde "fason" geçip geçmediğini tutar
  const [fasonFlags, setFasonFlags] = useState<Record<string, boolean | null>>(
    {},
  );

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Belge tasarım aracı ayrı bileşende (SendDocumentModal): satır satır kalem
  // girişi, ayarlardan gelen KDV oran listesi ve kalıcı sonuç/hata paneli.
  const [sendDocOrder, setSendDocOrder] = useState<Order | null>(null);
  const [standaloneOpen, setStandaloneOpen] = useState(false);
  // Elle fatura no (fatura modülü lisansta yokken; izin kodu sunucudan yalnız o durumda gelir)
  const [invoiceNoOrder, setInvoiceNoOrder] = useState<Order | null>(null);

  const handleOpenSendDocument = (order: Order) => setSendDocOrder(order);

  // Filtreleri localStorage'a kaydet
  useEffect(() => {
    const filtersToSave = {
      firmFilter,
      dateFilter,
      searchTerm,
      invoiceFilter,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtersToSave));
  }, [firmFilter, dateFilter, searchTerm, invoiceFilter]);

  // İlk açılışta ve firma/tarih süzgeci değişince siparişleri yeniden yükle
  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmFilter, dateFilter]);

  const closeMenu = () => {
    setOpenDropdownId(null);
    setDropdownPosition(null);
  };

  // Satır menüsü: dışarı tıklayınca / Esc ile kapanır
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".ao-more") && !target.closest(".ao-menu")) closeMenu();
    };
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeMenu(); };
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  // Scroll olduğunda menüyü kapat
  useEffect(() => {
    const handleScroll = () => {
      if (openDropdownId) closeMenu();
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [openDropdownId]);

  // Maliyet detayı: Esc ile kapanır
  useEffect(() => {
    if (!costDetailModal.isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); closeCostDetail(); } };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costDetailModal.isOpen]);

  // Sipariş maliyetlerinde "fason" geçen siparişleri tara
  const scanFasonFlags = async (orderList: Order[]) => {
    if (orderList.length === 0) return;
    // Hepsini loading olarak işaretle
    const loadingFlags: Record<string, boolean | null> = {};
    orderList.forEach((o) => {
      loadingFlags[o.orderId] = null;
    });
    setFasonFlags(loadingFlags);

    const results = await Promise.allSettled(
      orderList.map(async (order) => {
        const costs = await costService.getModelCosts(order.orderId);
        const hasFason = costs.some((c) => {
          const text = [c.costItemName, c.costCategoryName, c.usage, c.notes]
            .filter(Boolean)
            .join(" ");
          return turkishIncludes(text, "fason");
        });
        return [order.orderId, hasFason] as const;
      }),
    );

    const resolved: Record<string, boolean | null> = {};
    results.forEach((r, i) => {
      if (r.status === "fulfilled") {
        resolved[r.value[0]] = r.value[1];
      } else {
        resolved[orderList[i].orderId] = false;
      }
    });
    setFasonFlags(resolved);
  };

  const loadOrders = async () => {
    try {
      setLoading(true);

      // Tüm siparişleri çek (hem IsActive=true hem IsActive=false)
      const allOrders = await orderService.getInvoiced();

      // Sadece tamamlanmış siparişleri filtrele
      let filteredData = allOrders.filter(
        (order) => order.status === OrderStatus.Tamamlandi,
      );

      // Firma filtresi uygula
      if (firmFilter.length > 0) {
        filteredData = filteredData.filter(
          (order) => order.firmId && firmFilter.includes(order.firmId),
        );
      }

      // Tarih filtresi uygula
      if (dateFilter.startDate || dateFilter.endDate) {
        filteredData = filteredData.filter((order) => {
          if (!order.completionDate) return false;

          const orderDate = new Date(order.completionDate);
          const startDate = dateFilter.startDate
            ? new Date(dateFilter.startDate)
            : null;
          const endDate = dateFilter.endDate
            ? new Date(dateFilter.endDate)
            : null;

          if (startDate && orderDate < startDate) return false;
          if (endDate && orderDate > endDate) return false;

          return true;
        });
      }

      setOrders(filteredData);
      setLoadError(false);
      // Fason taramasını başlat (arka planda)
      scanFasonFlags(filteredData);
    } catch (error: any) {
      console.error("❌ Failed to load orders:", error);
      setLoadError(true);
      setAlertModal({
        isOpen: true,
        message: "Siparişler yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
      setFirstLoad(false);
    }
  };

  const getStatusText = (status?: OrderStatus): string => {
    if (status === undefined || status === null) return "Atanmadı";
    switch (status) {
      case OrderStatus.Atanmadi:
        return "Atanmadı";
      case OrderStatus.Islemde:
        return "İşlemde";
      case OrderStatus.Tamamlandi:
        return "Tamamlandı";
      case OrderStatus.IptalEdildi:
        return "İptal Edildi";
      default:
        return "Atanmadı";
    }
  };

  const getUnitText = (unit?: OrderUnit): string => {
    const unitMap: { [key: number]: string } = {
      [OrderUnit.Adet]: "Adet",
      [OrderUnit.Metre]: "Metre",
      [OrderUnit.Takim]: "Takım",
      [OrderUnit.Pano]: "Pano",
    };
    return unitMap[unit ?? OrderUnit.Adet] || "Adet";
  };

  // Arama ve fatura filtresi fonksiyonu
  const getFilteredOrders = (ordersToFilter: Order[]) => {
    let filtered = ordersToFilter;

    // Fatura filtresi uygula
    if (invoiceFilter === "filled") {
      filtered = filtered.filter(hasInvoiceNo);
    } else if (invoiceFilter === "empty") {
      filtered = filtered.filter((order) => !hasInvoiceNo(order));
    }

    // Arama filtresi uygula
    if (!searchTerm.trim()) return filtered;

    return filtered.filter((order) => {
      return (
        turkishIncludes(order.orderId || "", searchTerm) ||
        turkishIncludes(order.firm?.firmName || "", searchTerm) ||
        turkishIncludes(order.model?.modelName || "", searchTerm) ||
        turkishIncludes(order.model?.modelCode || "", searchTerm) ||
        turkishIncludes(order.invoice || "", searchTerm) ||
        turkishIncludes(order.invoiceNumber || "", searchTerm) ||
        turkishIncludes(getStatusText(order.status), searchTerm)
      );
    });
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  // Fatura bilgileri artik ELLE GIRILMIYOR: belge sistem uzerinden kesilir
  // (SendDocumentModal -> /Order/{id}/send-document) ve numara servisten gelip
  // siparise yazilir. Fatura kolonlari salt okunur gosterimdir.

  const processedOrders = getFilteredOrders(orders);

  const aoColDefs = useMemo(
    () => [
      { key: "orderId", value: (o: Order) => o.orderId || "" },
      { key: "firm", value: (o: Order) => o.firm?.firmName || "" },
      { key: "model", value: (o: Order) => o.model?.modelName || "" },
      { key: "quantity", type: "float", value: (o: Order) => o.quantity ?? null },
      { key: "unit", value: (o: Order) => getUnitText(o.unit) },
      { key: "price", type: "decimal", value: (o: Order) => o.price ?? null },
      { key: "total", type: "decimal", value: (o: Order) => (o.price && o.quantity ? o.price * o.quantity : null) },
      { key: "status", value: (o: Order) => getStatusText(o.status) },
      { key: "acceptanceDate", type: "datetime", value: (o: Order) => o.acceptanceDate || null },
      { key: "completionDate", type: "datetime", value: (o: Order) => o.completionDate || null },
      { key: "active", value: (o: Order) => (o.isActive ? "Aktif" : "Pasif") },
      { key: "invoice", value: (o: Order) => o.invoice || "" },
      { key: "invoiceNo", value: (o: Order) => o.invoiceNumber || "" },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const { ctx: aoCf, filtered: displayOrders } = useTableColumnFilters(processedOrders, aoColDefs);

  // KPI'lar ve çip sayıları yalnız yüklü satırlardan (firma/tarih süzgeci uygulanmış tamamlanan siparişler)
  const stats = useMemo(() => {
    const pending = orders.filter((o) => !hasInvoiceNo(o));
    const done = orders.length - pending.length;
    const now = new Date();
    const thisMonth = orders.filter((o) => {
      if (!o.completionDate) return false;
      const d = new Date(o.completionDate);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    });
    const flags = orders.map((o) => fasonFlags[o.orderId]);
    return {
      pending: pending.length,
      pendingAmount: sumByCurrency(pending),
      done,
      donePct: orders.length ? Math.round((done / orders.length) * 100) : 0,
      month: thisMonth.length,
      monthAmount: sumByCurrency(thisMonth),
      monthLabel: now.toLocaleDateString("tr-TR", { month: "long" }),
      fason: flags.filter((f) => f === true).length,
      fasonScanning: flags.some((f) => f === null || f === undefined),
    };
  }, [orders, fasonFlags]);
  const chipCount: Record<InvoiceFilter, number> = { all: orders.length, empty: stats.pending, filled: stats.done };
  const listAmount = sumByCurrency(displayOrders);

  const filterOn =
    firmFilter.length > 0 ||
    !!dateFilter.startDate ||
    !!dateFilter.endDate ||
    !!searchTerm ||
    invoiceFilter !== "all" ||
    aoCf.hasActive;

  const resetFilters = () => {
    setFirmFilter([]);
    setDateFilter({ startDate: "", endDate: "" });
    setSearchTerm("");
    setInvoiceFilter("all");
    aoCf.clearAll();
    localStorage.removeItem(STORAGE_KEY);
  };

  const toggleInvoiceFilter = (f: InvoiceFilter) => setInvoiceFilter((cur) => (cur === f ? "all" : f));

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, order: Order) => {
    e.stopPropagation();
    if (openDropdownId === order.orderId) return closeMenu();
    const rect = e.currentTarget.getBoundingClientRect();
    const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const menuHeight = 11 * rootPx; // ~4 öğe
    const menuWidth = 14 * rootPx; // menü genişliği 14rem
    const shouldOpenUp = window.innerHeight - rect.bottom < menuHeight;
    setDropdownPosition({
      top: shouldOpenUp ? Math.max(8, rect.top - menuHeight) : rect.bottom + 4,
      left: Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)),
    });
    setOpenDropdownId(order.orderId);
  };

  const menuOrder = openDropdownId ? orders.find((o) => o.orderId === openDropdownId) : undefined;
  const canSend = has("accounting-orders:send");

  if (loading && firstLoad) {
    return <PageLoader message="Siparişler yükleniyor..." />;
  }

  return (
    <div className="po-page">
      <header className="po-head">
        <div>
          <span className="po-kicker">Faturalama</span>
          <h1>Fatura Yönetimi</h1>
          <p>Tamamlanan siparişlerin faturasını kesin ya da fatura numarasını işleyin.</p>
        </div>
        <div className="po-head-actions">
          <button type="button" className="po-btn" onClick={loadOrders} disabled={loading}>
            <span className={loading ? "po-spin-icon" : ""} style={{ display: "inline-flex" }}>{I.refresh}</span> Yenile
          </button>
          {/* Siparişe bağlı olmayan satış: numune, hizmet, kumaş/hurda satışı… */}
          {hasModule("invoicing") && has("e-documents:send") && (
            <button type="button" className="po-btn primary" onClick={() => setStandaloneOpen(true)}>
              {I.plus} Sipariş dışı fatura
            </button>
          )}
        </div>
      </header>

      <section className="po-kpis ao-kpis">
        <button type="button" className={`po-kpi ao-warn ${invoiceFilter === "empty" ? "on" : ""}`} aria-pressed={invoiceFilter === "empty"} onClick={() => toggleInvoiceFilter("empty")}>
          <span className="l">Faturalanacak</span>
          <span className="v">{stats.pending}<small> sipariş</small></span>
          <span className="s">{stats.pendingAmount || "fatura no girilmemiş"}</span>
        </button>
        <button type="button" className={`po-kpi ok ${invoiceFilter === "filled" ? "on" : ""}`} aria-pressed={invoiceFilter === "filled"} onClick={() => toggleInvoiceFilter("filled")}>
          <span className="l">Faturalandı</span>
          <span className="v">{stats.done}<small> sipariş</small></span>
          <span className="s">tamamlananlar içinde <b>%{stats.donePct}</b></span>
        </button>
        <div className="po-kpi navy">
          <span className="l">Bu ay tamamlanan</span>
          <span className="v">{stats.month}<small> sipariş</small></span>
          <span className="s">{stats.monthAmount || `${stats.monthLabel} ayında tutar yok`}</span>
        </div>
        <div className="po-kpi">
          <span className="l">Fason maliyetli</span>
          <span className="v">{stats.fasonScanning ? "…" : stats.fason}{!stats.fasonScanning && <small> sipariş</small>}</span>
          <span className="s">{stats.fasonScanning ? "maliyetler taranıyor" : "maliyetinde fason var"}</span>
        </div>
      </section>

      <section className={`po-card ${loading ? "dim" : ""}`}>
        <div className="po-toolbar">
          <div className="po-search">
            {I.search}
            <input
              placeholder="Firma, model, fatura no veya sipariş ID ara"
              aria-label="Sipariş ara"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && <button type="button" className="po-ib" aria-label="Aramayı temizle" onClick={() => setSearchTerm("")}>{I.close}</button>}
          </div>
          <FirmMultiPickerField
            id="acc-firm-filter"
            variant="toolbar"
            value={firmFilter}
            nullLabel="Tüm firmalar"
            allowCreate={false}
            title="Firmaya göre süz"
            onChange={(ids) => setFirmFilter(ids)}
          />
          <div className="ao-dates" title="Tamamlanma tarihi aralığı">
            <input
              type="date"
              aria-label="Tamamlanma başlangıç tarihi"
              value={dateFilter.startDate}
              onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
            />
            <span>–</span>
            <input
              type="date"
              aria-label="Tamamlanma bitiş tarihi"
              value={dateFilter.endDate}
              onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
            />
          </div>
          <div className="po-chipgroup" role="group" aria-label="Fatura durumu">
            {INVOICE_CHIPS.map(([k, l]) => (
              <button type="button" key={k} className={invoiceFilter === k ? "on" : ""} aria-pressed={invoiceFilter === k} onClick={() => setInvoiceFilter(k)}>
                {l}<i>{chipCount[k]}</i>
              </button>
            ))}
          </div>
          {filterOn && <button type="button" className="po-clear" onClick={resetFilters} title="Tüm filtreleri temizle">{I.filterOff} Temizle</button>}
        </div>

        <div className="po-card-head">
          <span><b>{displayOrders.length}</b> tamamlanan sipariş{displayOrders.length !== orders.length && <> · toplam {orders.length}</>}</span>
          {listAmount && <span className="po-hint po-hide-sm">Tutar: <b>{listAmount}</b></span>}
        </div>

        {displayOrders.length === 0 ? (
          loadError && orders.length === 0 ? (
            <div className="po-blank"><span className="po-blank-icon">{I.info}</span><b>Siparişler yüklenemedi</b>Bağlantıyı kontrol edip yeniden deneyin.
              <button type="button" className="po-btn" onClick={loadOrders} disabled={loading}>{I.refresh} Yeniden dene</button></div>
          ) : orders.length === 0 && !filterOn ? (
            <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Tamamlanmış sipariş bulunmuyor</b>Sipariş tamamlandığında faturalamak için burada listelenir.</div>
          ) : (
            <div className="po-blank"><span className="po-blank-icon">{I.search}</span><b>Arama kriterine uygun sipariş bulunamadı</b>Süzgeci değiştirin ya da temizleyin.
              <button type="button" className="po-btn" onClick={resetFilters}>{I.filterOff} Süzgeci sıfırla</button></div>
          )
        ) : (
          <div className="po-table-wrap">
            <table className="po-table ao-table">
              <thead>
                <tr>
                  <FilterTh label="Firma" colKey="firm" ctx={aoCf} />
                  <FilterTh label="Model" colKey="model" ctx={aoCf} className="po-hide-sm" />
                  <th className="po-hide-md">Fason</th>
                  <FilterTh label="Miktar" colKey="quantity" type="float" ctx={aoCf} className="num" />
                  <FilterTh label="Birim Fiyat" colKey="price" type="decimal" ctx={aoCf} className="num po-hide-lg" />
                  <FilterTh label="Toplam" colKey="total" type="decimal" ctx={aoCf} className="num" />
                  <FilterTh label="Kabul" colKey="acceptanceDate" type="datetime" ctx={aoCf} className="po-hide-lg" />
                  <FilterTh label="Tamamlanma" colKey="completionDate" type="datetime" ctx={aoCf} className="po-hide-md" />
                  <FilterTh label="Fatura" colKey="invoice" ctx={aoCf} className="po-hide-md" />
                  <FilterTh label="Fatura No" colKey="invoiceNo" ctx={aoCf} align="right" className="num" />
                  <th className="act" aria-label="İşlemler" />
                </tr>
              </thead>
              <tbody>
                {displayOrders.map((order) => {
                  const cur = orderCurrency(order);
                  const fason = fasonFlags[order.orderId];
                  return (
                    <tr
                      key={order.orderId}
                      className={openDropdownId === order.orderId ? "on" : ""}
                      onClick={() => setDetailModal({ isOpen: true, orderId: order.orderId })}
                    >
                      <td>
                        <div className="po-row">
                          <CopyIdButton value={order.orderId} />
                          <div>
                            <div className="po-name">
                              {order.firm?.firmName || "—"}
                              {!order.isActive && <span className="po-badge muted">Pasif</span>}
                            </div>
                            <div className="po-sub po-only-sm">{order.model?.modelName || "—"}</div>
                            {fason === true && <div className="po-sub po-only-sm">Fason</div>}
                          </div>
                        </div>
                      </td>
                      <td className="po-hide-sm">
                        <div className="po-name">{order.model?.modelName || "—"}</div>
                        {order.model?.modelCode && order.model.modelCode !== order.model.modelName && <div className="po-sub">{order.model.modelCode}</div>}
                      </td>
                      <td className="po-hide-md">
                        {fason === null || fason === undefined ? (
                          <span className="po-sub" title="Maliyetler taranıyor">…</span>
                        ) : fason ? (
                          <span className="po-badge info" title="Sipariş maliyetlerinde fason kaydı var">Fason</span>
                        ) : (
                          <span className="po-sub">—</span>
                        )}
                      </td>
                      <td className="num">
                        <span className="po-amt">{(order.quantity || 0).toLocaleString("tr-TR")}</span>
                        <div className="po-sub">{getUnitText(order.unit)}</div>
                      </td>
                      <td className="num po-hide-lg">{order.price ? `${fmt2(order.price)} ${cur}` : "—"}</td>
                      <td className="num">
                        {order.price && order.quantity ? <span className="po-amt">{fmt2(order.price * order.quantity)} {cur}</span> : <span className="po-sub">—</span>}
                      </td>
                      <td className="po-due po-hide-lg">{formatDate(order.acceptanceDate)}</td>
                      <td className="po-due po-hide-md">{formatDate(order.completionDate)}</td>
                      <td className="po-hide-md">{order.invoice || <span className="po-sub">—</span>}</td>
                      <td className="num">
                        {order.invoiceNumber ? (
                          <span className="po-badge ok ao-no" title="Belge kesildi; yeniden kesmek için Kesilen Belgeler'den iptal edin">{order.invoiceNumber}</span>
                        ) : (
                          <span className="po-badge warn">Bekliyor</span>
                        )}
                      </td>
                      <td className="act">
                        <div className="po-rowacts" onClick={(e) => e.stopPropagation()}>
                          {canSend && !order.invoiceNumber && (
                            <button type="button" className="po-btn small" onClick={() => { closeMenu(); handleOpenSendDocument(order); }}>
                              {I.doc} Fatura oluştur
                            </button>
                          )}
                          <button type="button" className="po-ib" title="Detay" aria-label="Sipariş detayı" onClick={() => setDetailModal({ isOpen: true, orderId: order.orderId })}>{I.eye}</button>
                          <button
                            type="button"
                            className="po-ib ao-more"
                            title="Diğer işlemler"
                            aria-label="Diğer işlemler"
                            aria-haspopup="menu"
                            aria-expanded={openDropdownId === order.orderId}
                            onClick={(e) => openMenu(e, order)}
                          >
                            {I.more}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Satır menüsü */}
      {menuOrder && dropdownPosition && (
        <div className="ao-menu" role="menu" style={{ top: dropdownPosition.top, left: dropdownPosition.left }}>
          <button type="button" role="menuitem" onClick={() => { closeMenu(); openCostDetail(menuOrder); }}>
            {I.money}<span>Maliyet detayları</span>
          </button>
          {has("accounting-orders:update") && (
            <button type="button" role="menuitem" onClick={() => { closeMenu(); setInvoiceNoOrder(menuOrder); }}>
              {I.pen}<span>Fatura no gir</span>
            </button>
          )}
          {canSend && menuOrder.invoiceNumber && (
            <button type="button" role="menuitem" disabled title={`Belge kesildi: ${menuOrder.invoiceNumber}. Yeniden kesmek için önce Kesilen Belgeler'den iptal edin.`}>
              {I.doc}<span>Belge kesildi · {menuOrder.invoiceNumber}</span>
            </button>
          )}
          <hr />
          {hasModule("shipping") && has("shipping:create") && (
            <button type="button" role="menuitem" onClick={() => { closeMenu(); navigate(`/shipping/new?firmId=${menuOrder.firmId}&orderId=${menuOrder.orderId}`); }}>
              {I.truck}<span>Sevkiyat oluştur</span>
            </button>
          )}
          <button type="button" role="menuitem" onClick={() => { closeMenu(); setSelectedOrderForImages(menuOrder.orderId); setShowImageManager(true); }}>
            {I.image}<span>Resimleri görüntüle</span>
          </button>
        </div>
      )}

      {showImageManager && selectedOrderForImages && (
        <ImageManager
          orderId={selectedOrderForImages}
          onClose={() => {
            setShowImageManager(false);
            setSelectedOrderForImages(null);
          }}
        />
      )}

      {/* Order Detail Modal */}
      <OrderDetailModal
        isOpen={detailModal.isOpen}
        orderId={detailModal.orderId}
        onClose={() => setDetailModal({ isOpen: false, orderId: "" })}
      />

      {/* Maliyet Detay Modalı */}
      {costDetailModal.isOpen && (
        <div className="po-overlay center" onMouseDown={(e) => { if (e.target === e.currentTarget) closeCostDetail(); }}>
          <div className="po-modal xl ao-cost" role="dialog" aria-modal="true" aria-labelledby="ao-cost-title">
            <div className="po-modal-head">
              <div>
                <h3 id="ao-cost-title">Maliyet detayları</h3>
                <div className="po-badges ao-cost-sub">
                  {costDetailModal.firmName && <span className="po-badge muted">{costDetailModal.firmName}</span>}
                  <span className="po-badge info">Model {costDetailModal.modelCode || "—"}</span>
                  <span className="po-badge muted">{costDetailModal.quantity.toLocaleString("tr-TR")} adet</span>
                </div>
              </div>
              <button type="button" className="po-ib big" aria-label="Kapat" onClick={closeCostDetail}>{I.close}</button>
            </div>

            <div className="po-modal-body">
              {costDetailModal.loading ? (
                <div className="po-loading"><span className="po-spin" /> Maliyet kalemleri yükleniyor…</div>
              ) : costDetailModal.costs.length === 0 ? (
                <div className="po-blank"><span className="po-blank-icon">{I.info}</span><b>Maliyet kalemi yok</b>Bu sipariş için maliyet kalemi bulunamadı.</div>
              ) : (
                (() => {
                  const groupedByWorkshop = costDetailModal.costs.reduce(
                    (acc, cost) => {
                      const workshopName =
                        cost.order?.workshop?.name || cost.usage || "Genel";
                      if (!acc[workshopName]) acc[workshopName] = [];
                      acc[workshopName].push(cost);
                      return acc;
                    },
                    {} as Record<string, typeof costDetailModal.costs>,
                  );
                  return (
                    <>
                      {Object.keys(groupedByWorkshop).map((workshopName) => {
                        const costs = groupedByWorkshop[workshopName];
                        const workshopTotal = costs.reduce((sum, c) => sum + convertCostToTRY(c), 0);
                        return (
                          <div key={workshopName} className="po-sec">
                            <h3>{workshopName}<em>{costs.length} kalem</em><b>{formatCurrency(workshopTotal, "TRY")}</b></h3>
                            <div className="po-table-wrap tight">
                              <table className="po-table small">
                                <thead>
                                  <tr>
                                    <th>Maliyet kalemi</th>
                                    <th className="po-hide-sm">Kategori</th>
                                    <th className="num">Miktar</th>
                                    <th className="po-hide-sm">Birim</th>
                                    <th className="num po-hide-md">Fire</th>
                                    <th className="num po-hide-md">Birim fiyat</th>
                                    <th className="num">Toplam</th>
                                    <th className="num">TL karşılığı</th>
                                    <th className="po-hide-md">Not</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {costs.map((cost) => {
                                    const tryAmount = convertCostToTRY(cost);
                                    return (
                                      <tr key={cost.modelCostId} className="static">
                                        <td><span className="po-name">{cost.costItemName || "—"}</span></td>
                                        <td className="po-hide-sm">{cost.costCategoryName ? <span className="po-badge muted">{cost.costCategoryName}</span> : "—"}</td>
                                        <td className="num">
                                          {cost.quantity}
                                          {cost.quantity2 && ` × ${cost.quantity2}`}
                                        </td>
                                        <td className="po-hide-sm">
                                          {cost.unit}
                                          {cost.unit2 && ` × ${cost.unit2}`}
                                        </td>
                                        <td className="num po-hide-md">{cost.wastagePercentage ? `%${cost.wastagePercentage}` : "—"}</td>
                                        <td className="num po-hide-md">{formatCurrency(cost.unitPrice || 0, cost.currency || "TRY")}</td>
                                        <td className="num"><span className="po-amt">{formatCurrency(cost.totalCost || 0, cost.currency || "TRY")}</span></td>
                                        <td className="num">
                                          {formatCurrency(tryAmount, "TRY")}
                                          {cost.currency !== "TRY" && (cost.usdRate || cost.eurRate) && (
                                            <span className="ao-rate">
                                              Kur:{" "}
                                              {cost.currency === "USD"
                                                ? cost.usdRate?.toFixed(4)
                                                : cost.currency === "EUR"
                                                  ? cost.eurRate?.toFixed(4)
                                                  : "-"}{" "}
                                              TL
                                            </span>
                                          )}
                                        </td>
                                        <td className="po-hide-md">{cost.notes ? <div className="ao-note">{cost.notes}</div> : <span className="po-sub">—</span>}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })}
                      <div className="po-totals">
                        <div className="po-total-row"><span>Genel toplam</span><b>{formatCurrency(costDetailModal.totalCost, "TRY")}</b></div>
                        <div className="po-total-row"><span>Genel gider dahil</span><b>{formatCurrency(costDetailModal.costWithOverhead, "TRY")}</b></div>
                        {costDetailModal.quantity > 0 && (
                          <div className="po-total-row"><span>Birim maliyet</span><b>{formatCurrency(costDetailModal.costWithOverhead / costDetailModal.quantity, "TRY")}</b></div>
                        )}
                        <div className="po-total-row grand"><span>Kâr dahil (nihai)</span><b>{formatCurrency(costDetailModal.finalCostWithProfit, "TRY")}</b></div>
                      </div>
                    </>
                  );
                })()
              )}
            </div>

            <div className="po-modal-foot">
              <button type="button" className="po-btn" onClick={closeCostDetail}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Elle fatura no (fatura modülü yokken) */}
      {invoiceNoOrder && (
        <InvoiceNumberModal
          order={invoiceNoOrder}
          onClose={() => setInvoiceNoOrder(null)}
          onSaved={(invoice, invoiceNumber) => {
            setOrders((prev) =>
              prev.map((o) =>
                o.orderId === invoiceNoOrder.orderId
                  ? { ...o, invoice: invoice || undefined, invoiceNumber: invoiceNumber || undefined }
                  : o,
              ),
            );
          }}
        />
      )}

      {/* Sipariş dışı fatura — kesilen belge Kesilen Belgeler'de listelenir */}
      {standaloneOpen && (
        <SendDocumentModal onClose={() => setStandaloneOpen(false)} onSent={() => {}} />
      )}

      {/* Belge tasarım aracı */}
      {sendDocOrder && (
        <SendDocumentModal
          order={sendDocOrder}
          onClose={() => setSendDocOrder(null)}
          onSent={(result, appliedRate) => {
            // Tabloya işle: numara servisten gelir.
            setOrders((prev) =>
              prev.map((o) =>
                o.orderId === sendDocOrder.orderId
                  ? {
                      ...o,
                      invoiceNumber: result.documentNumber || o.invoiceNumber,
                      vatRate: appliedRate ?? o.vatRate,
                    }
                  : o,
              ),
            );
          }}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
};

export default AccountingOrders;
